import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageSquare, MapPin, Clock, Send, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { contactAPI } from '../lib/api';
import { SiteNav } from '../components/SiteNav';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_rejuven-health/artifacts/rqq06olv_Rejuven%20Logo%20-%20Website.png';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const infoCards = [
  { icon: Mail, wrap: 'bg-[#E8D5D1]', iconColor: 'text-[#C28B87]', title: 'Email Us', sub: 'For general inquiries', linkLabel: 'hello@rejuvenhealth.com', href: 'mailto:hello@rejuvenhealth.com' },
  { icon: MessageSquare, wrap: 'bg-[#F3EFEF]', iconColor: 'text-[#899285]', title: 'Support', sub: 'Technical questions', linkLabel: 'support@rejuvenhealth.com', href: 'mailto:support@rejuvenhealth.com' },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await contactAPI.submit(formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  return (
    <div className="min-h-screen bg-[#FAF8F7] text-[#2B2625]">
      <SiteNav />

      {/* Hero */}
      <section className="py-16 lg:py-20 relative overflow-hidden">
        <div className="absolute -top-16 right-0 w-[400px] h-[400px] bg-[#E8D5D1]/50 rounded-full blur-[130px]" />
        <div className="max-w-6xl mx-auto px-6 lg:px-8 relative">
          <motion.div className="text-center max-w-2xl mx-auto" initial="hidden" animate="visible" variants={fadeInUp}>
            <p className="text-[#C28B87] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-4">Contact Us</p>
            <h1 className="font-display text-4xl lg:text-5xl text-[#2B2625] mb-6 font-medium">We&apos;d love to hear from you</h1>
            <p className="text-lg text-[#6C6361]">Have questions about Rejuven or our approach to precision wellness? Reach out and we&apos;ll get back to you soon.</p>
          </motion.div>
        </div>
      </section>

      {/* Contact */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            <motion.div className="lg:col-span-1 space-y-5" initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              {infoCards.map((c) => (
                <div key={c.title} className="bg-white rounded-2xl p-6 border border-[#E8D5D1]/70 hover:shadow-[0_20px_44px_-20px_rgba(43,38,37,0.12)] transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl ${c.wrap} flex items-center justify-center flex-shrink-0`}><c.icon className={`w-5 h-5 ${c.iconColor}`} /></div>
                    <div>
                      <h3 className="font-semibold text-[#2B2625] mb-1">{c.title}</h3>
                      <p className="text-sm text-[#6C6361] mb-2">{c.sub}</p>
                      <a href={c.href} className="text-[#2B2625] hover:text-[#C28B87] text-sm font-medium transition-colors">{c.linkLabel}</a>
                    </div>
                  </div>
                </div>
              ))}
              <div className="bg-white rounded-2xl p-6 border border-[#E8D5D1]/70">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E8D5D1] flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-[#C28B87]" /></div>
                  <div><h3 className="font-semibold text-[#2B2625] mb-1">Location</h3><p className="text-sm text-[#6C6361]">Rejuven Inc.<br />2261 Market Street STE 61210<br />San Francisco, CA 94114</p></div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-[#E8D5D1]/70">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#F3EFEF] flex items-center justify-center flex-shrink-0"><Clock className="w-5 h-5 text-[#899285]" /></div>
                  <div><h3 className="font-semibold text-[#2B2625] mb-1">Response Time</h3><p className="text-sm text-[#6C6361]">We typically respond within 24-48 hours during business days.</p></div>
                </div>
              </div>
            </motion.div>

            <motion.div className="lg:col-span-2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>
              <div className="bg-white rounded-[2rem] p-8 lg:p-10 border border-[#E8D5D1]/70 shadow-[0_8px_32px_rgba(43,38,37,0.05)]">
                {submitted ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 rounded-full bg-[#F3EFEF] flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-10 h-10 text-[#C28B87]" /></div>
                    <h3 className="font-display text-2xl text-[#2B2625] mb-3 font-medium">Message Sent!</h3>
                    <p className="text-[#6C6361] mb-8 max-w-md mx-auto">Thank you for reaching out. We&apos;ll get back to you as soon as possible.</p>
                    <Button onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', subject: '', message: '' }); }} variant="outline" className="rounded-full border-[#E8D5D1] text-[#2B2625] px-8">Send Another Message</Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2"><Label htmlFor="name" className="text-[#2B2625]">Your Name</Label><Input id="name" name="name" type="text" placeholder="Jane Doe" value={formData.name} onChange={handleChange} className="rounded-xl border-[#E8D5D1] focus:border-[#C28B87] bg-[#FAF8F7] h-12" required data-testid="contact-name-input" /></div>
                      <div className="space-y-2"><Label htmlFor="email" className="text-[#2B2625]">Email Address</Label><Input id="email" name="email" type="email" placeholder="jane@example.com" value={formData.email} onChange={handleChange} className="rounded-xl border-[#E8D5D1] focus:border-[#C28B87] bg-[#FAF8F7] h-12" required data-testid="contact-email-input" /></div>
                    </div>
                    <div className="space-y-2"><Label htmlFor="subject" className="text-[#2B2625]">Subject</Label><Input id="subject" name="subject" type="text" placeholder="How can we help?" value={formData.subject} onChange={handleChange} className="rounded-xl border-[#E8D5D1] focus:border-[#C28B87] bg-[#FAF8F7] h-12" required data-testid="contact-subject-input" /></div>
                    <div className="space-y-2"><Label htmlFor="message" className="text-[#2B2625]">Message</Label><textarea id="message" name="message" rows={6} placeholder="Tell us about your inquiry or question about Rejuven..." value={formData.message} onChange={handleChange} className="w-full rounded-xl border border-[#E8D5D1] bg-[#FAF8F7] px-4 py-3 text-[#2B2625] placeholder:text-[#6C6361]/60 focus:outline-none focus:ring-2 focus:ring-[#C28B87]/20 focus:border-[#C28B87] resize-none transition-[border-color,box-shadow]" required data-testid="contact-message-input" /></div>
                    <Button type="submit" disabled={loading} className="w-full bg-[#C28B87] text-white hover:bg-[#b07b77] rounded-full h-14 text-lg font-medium shadow-[0_10px_30px_rgba(194,139,135,0.3)]" data-testid="contact-submit-btn">
                      {loading ? (<span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />Sending...</span>) : (<span className="flex items-center gap-2">Send Message <Send className="w-5 h-5" /></span>)}
                    </Button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Teaser */}
      <section className="py-16 bg-[#F3EFEF]/50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl text-[#2B2625] mb-4 font-medium">Curious how it works?</h2>
          <p className="text-[#6C6361] mb-8">Discover how Rejuven turns your genotype and microbiome signals into personalized, preventative wellness guidance.</p>
          <Link to="/#how-it-works"><Button variant="outline" className="rounded-full border-[#E8D5D1] text-[#2B2625] px-8">Explore the Science</Button></Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#221D1C] text-white py-14 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-8">
            <div className="bg-white/10 rounded-2xl px-6 py-3"><img src={LOGO_URL} alt="Rejuven" className="h-8" /></div>
            <div className="flex items-center gap-8 text-sm text-white/55">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
            </div>
            <p className="text-white/35 text-sm">© 2026 Rejuven Inc.</p>
          </div>
          <div className="border-t border-white/10 pt-6">
            <p className="text-white/45 text-xs leading-relaxed max-w-4xl mx-auto text-center"><strong className="text-white/60">Medical disclaimer:</strong> Rejuven provides educational and preventative wellness information only. It is not medical advice and is not a substitute for professional diagnosis, treatment or the advice of your licensed physician. We do not diagnose, treat, cure or prevent any disease, and do not prescribe medications, hormones or peptides. Always consult a qualified healthcare provider before making changes to your health regimen.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
